import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { callAPI, Method } from '../commonFunctions/CommonFunctions';
import socket from '../commonFunctions/socket';


// Types
type ChartData = any[];
type TableData = any[];

interface KPIStatus {
    count: number;
    percentage: string;
}

interface KPIs {
    totalLoans: number;
    totalLoanAmount: number;
    loansToday: KPIStatus;
    totalRepaymentAmount: number;
    totalRepayments:number;
    totalPrincipal: number;
    totalInterest: number;
    repaymentAmountToday: KPIStatus;
    repaymentsToday:number;
    loanStatusCounts: {
        _id: string;
        count: number;
        percentage: string;
    }[];
}

interface DashboardState {
    loading: boolean;
    error: string | null;
    kpis: KPIs;
    charts: {
        paymentMethodStats: ChartData;
        monthlyLoanSanction: ChartData;
        monthlyRepayments: ChartData;
        userRoles: ChartData;
    };
    tables: {
        recentLoans: TableData;
        recentRepayments: TableData;
        collectionByBlocks: TableData;
        topCollectors: TableData;
    };
}

interface DashboardContextType extends DashboardState {
    fetchDashboardData: () => void;
    clearDashboardError: () => void;
}

// Context
const DashboardContext = createContext<DashboardContextType | null>(null);

// Initial State
const initialState: DashboardState = {
    loading: false,
    error: null,
    kpis: {
        totalLoans: 0,
        totalLoanAmount: 0,
        loansToday: { count: 0, percentage: "0%" },
        totalRepaymentAmount: 0,
        totalRepayments:0,
        totalPrincipal: 0,
        totalInterest: 0,
        repaymentAmountToday: { count: 0, percentage: "0%" },
        repaymentsToday:0,
        loanStatusCounts: []
    },
    charts: {
        paymentMethodStats: [],
        monthlyLoanSanction: [],
        monthlyRepayments: [],
        userRoles: [],
    },
    tables: {
        recentLoans: [],
        recentRepayments: [],
        collectionByBlocks: [],
        topCollectors: [],
    }
};

// Reducer
function dashboardReducer(state: any, action: any) {
    switch (action.type) {
        case 'FETCH_START':
            return { ...state, loading: true, error: null };
        case 'FETCH_SUCCESS':
            return { ...state, ...action.payload, loading: false };
        case 'FETCH_ERROR':
            return { ...state, loading: false, error: action.payload };
        case 'CLEAR_ERROR':
            return { ...state, error: null };
        default:
            return state;
    }
}

// Provider Component
export const DashboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(dashboardReducer, initialState);

    // Fetch dashboard data
    const fetchDashboardData = async () => {
        dispatch({ type: 'FETCH_START' });
        try {
            const res = await callAPI("dashboards/data", Method.GET);
            dispatch({ type: 'FETCH_SUCCESS', payload: res.data });
        } catch (error: any) {
            dispatch({
                type: 'FETCH_ERROR',
                payload: error.response?.data?.message || 'Fetch failed, please try again!',
            });
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    useEffect(() => {
        const handleFetch = () => { fetchDashboardData() }

        socket.on("repayment-done", handleFetch);
        socket.on("request-done", handleFetch);
        socket.on("new-loan", handleFetch);
        socket.on("loan-deleted", handleFetch)
        socket.on("loan-status-updated", handleFetch);
        socket.on("loan-updated", handleFetch);

        return () => {
            socket.off("repayment-done", handleFetch);
            socket.off("request-done", handleFetch);
            socket.off("new-loan", handleFetch);
            socket.off("loan-deleted", handleFetch);
            socket.off("loan-status-updated", handleFetch);
            socket.off("loan-updated", handleFetch);
        };
    }, []);

    const clearDashboardError = () => dispatch({ type: 'CLEAR_ERROR' });

    return (
        <DashboardContext.Provider value={{ ...state, fetchDashboardData, clearDashboardError }}>
            {children}
        </DashboardContext.Provider>
    );
};

export const useDashboard = () => {
    const context = useContext(DashboardContext);
    if (!context) {
        throw new Error("useDashboard must be used within a DashboardProvider");
    }
    return context;
};